import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Banknote, Bell, Building2, CalendarClock, CarFront, FileText, Globe, LayoutDashboard,
  LogOut, Map, Menu, Plus, Quote, Search, Settings, UserRound, Users, X, BookOpen } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { useRobots } from "@/components/seo/Seo";
import Logo from "@/components/Logo";
import { Avatar, T } from "@/components/admin/ui";

const NAV_GROUPS = [
  { label: "Opérations", items: [
    { to: "/admin/app", label: "Vue d'ensemble", icon: LayoutDashboard, end: true },
    { to: "/admin/app/bookings", label: "Réservations", icon: CalendarClock },
    { to: "/admin/app/dispatch", label: "Dispatch", icon: Map },
  ]},
  { label: "CRM", items: [
    { to: "/admin/app/customers", label: "Clients", icon: Users },
    { to: "/admin/app/business", label: "Business", icon: Building2 },
  ]},
  { label: "Flotte", items: [
    { to: "/admin/app/drivers", label: "Chauffeurs", icon: UserRound },
    { to: "/admin/app/vehicles", label: "Véhicules", icon: CarFront },
  ]},
  { label: "Finance", items: [
    { to: "/admin/app/quotes", label: "Devis", icon: Quote },
    { to: "/admin/app/invoices", label: "Factures", icon: FileText },
    { to: "/admin/app/payments", label: "Paiements", icon: Banknote },
    { to: "/admin/app/finance", label: "Pilotage", icon: Banknote },
  ]},
  { label: "Système", items: [
    { to: "/admin/app/seo", label: "SEO", icon: Globe },
    { to: "/admin/app/guides", label: "Guides", icon: BookOpen },
    { to: "/admin/app/communications", label: "Communications", icon: Bell },
    { to: "/admin/app/settings", label: "Réglages", icon: Settings },
  ]},
];

function CommandBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return undefined; }
    const timer = setTimeout(() => {
      adminApi.get("/search", { params: { q } }).then((r) => { setResults(r.data.results); setOpen(true); }).catch(() => {});
    }, 220);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); inputRef.current?.blur(); navigate(path); };

  const sections = results ? [
    ["Réservations", results.bookings, (b) => ({
      key: b.id, main: `${b.time || ""} · ${b.pickup} → ${b.dropoff || "à l'heure"}`,
      sub: `${b.customer?.first_name || ""} ${b.customer?.last_name || ""} · ${b.tracking_code}`,
      to: `/admin/app/bookings/${b.id}`, testId: `search-booking-${b.tracking_code}`,
    })],
    ["Clients", results.customers, (c) => ({
      key: c.id, main: `${c.first_name} ${c.last_name}`, sub: c.email || c.phone,
      to: `/admin/app/customers/${c.id}`, testId: `search-customer-${c.id}`,
    })],
    ["Chauffeurs", results.drivers, (d) => ({
      key: d.id, main: `${d.first_name} ${d.last_name}`, sub: d.phone,
      to: `/admin/app/drivers/${d.id}`, testId: `search-driver-${d.id}`,
    })],
    ["Véhicules", results.vehicles, (v) => ({
      key: v.id, main: `${v.make} ${v.model}`, sub: v.plate,
      to: "/admin/app/vehicles", testId: `search-vehicle-${v.id}`,
    })],
    ["Entreprises", results.organizations, (o) => ({
      key: o.id, main: o.name, sub: "", to: `/admin/app/business/${o.id}`, testId: `search-org-${o.id}`,
    })],
    ["Factures", results.invoices, (i) => ({
      key: i.id, main: i.number, sub: `CHF ${i.amount}`, to: "/admin/app/invoices", testId: `search-invoice-${i.id}`,
    })],
  ] : [];

  return (
    <div ref={boxRef} className="relative w-full max-w-xl" data-testid="admin-global-search">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate/60" strokeWidth={1.5} />
      <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        placeholder="Rechercher une réservation, un client, un chauffeur…"
        data-testid="admin-search-input" aria-label="Recherche globale"
        className="h-11 w-full rounded-xl border border-ink/[0.08] bg-ink/[0.03] pl-10 pr-14 text-sm text-ink placeholder:text-slate/60 outline-none transition-all duration-150 focus:border-miles/40 focus:bg-white focus:ring-4 focus:ring-miles/10" />
      <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 rounded-md border border-ink/10 bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate sm:flex">⌘K</kbd>
      {open && results && (
        <div className="absolute left-0 right-0 top-[52px] z-50 max-h-[420px] overflow-auto rounded-2xl border border-ink/[0.08] bg-white/95 p-1.5 shadow-[0_16px_40px_rgba(16,24,40,0.12)] backdrop-blur-xl" data-testid="admin-search-results">
          {sections.map(([label, list, map]) => list?.length > 0 && (
            <div key={label} className="mb-1">
              <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate/60">{label}</p>
              {list.map((item) => {
                const r = map(item);
                return (
                  <button key={r.key} type="button" onClick={() => go(r.to)} data-testid={r.testId}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition-colors duration-100 hover:bg-miles-ice/60">
                    <span className="truncate text-sm font-medium text-ink">{r.main}</span>
                    <span className="truncate pl-3 text-xs text-slate">{r.sub}</span>
                  </button>
                );
              })}
            </div>
          ))}
          {!sections.some(([, list]) => list?.length > 0) && (
            <p className="px-3 py-6 text-center text-sm text-slate" data-testid="search-empty">Aucun résultat pour « {q} ».</p>
          )}
        </div>
      )}
    </div>
  );
}

const LEVEL_DOT = { high: "bg-red-500", medium: "bg-amber-500", low: "bg-slate/50" };
const LEVEL_GROUP = { high: "Urgent", medium: "Opérations", low: "Système" };

export default function AdminShell() {
  useRobots("noindex, nofollow");
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [attention, setAttention] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const bellRef = useRef(null);
  const userRef = useRef(null);

  useEffect(() => {
    document.title = "MILES Operations";
    adminApi.get("/auth/me")
      .then((r) => setUser(r.data.user))
      .catch(() => navigate("/admin", { replace: true }));
    adminApi.get("/overview").then((r) => {
      const list = r.data.alerts || [];
      setAlerts(list);
      setAttention((r.data.today?.unassigned || 0) + list.filter((a) => a.type !== "unassigned").length);
    }).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserMenu(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const logout = async () => {
    await adminApi.post("/auth/logout").catch(() => {});
    navigate("/admin", { replace: true });
  };

  const firstName = (user?.name || "Admin").split(" ")[0];

  const navContent = (onNavigate, idPrefix = "") => NAV_GROUPS.map((group) => (
    <div key={group.label} className="mb-4">
      <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate/50">{group.label}</p>
      <div className="space-y-0.5">
        {group.items.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate}
            data-testid={`${idPrefix}admin-nav-${item.to.split("/").pop() || "overview"}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-all duration-150 ${
              isActive ? "bg-miles-ice font-semibold text-ink" : "text-slate hover:bg-ink/[0.04] hover:text-ink"}`}>
            {({ isActive }) => (
              <>
                <item.icon className={`h-[17px] w-[17px] ${isActive ? "text-miles" : "text-slate/70"}`} strokeWidth={1.5} />
                {item.label}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  ));

  return (
    <div className="flex min-h-screen bg-cloud" data-testid="admin-shell">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[248px] flex-col border-r border-ink/[0.06] bg-white lg:flex">
        <div className="flex h-16 items-center gap-2 px-5">
          <Logo variant="blue" className="h-[22px]" testId="admin-logo" />
          <span className="rounded-md bg-miles-ice px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-miles">Ops</span>
        </div>
        <nav className="flex-1 overflow-auto px-3 pb-3">{navContent()}</nav>
        <div className="p-3">
          <div ref={userRef} className="relative">
            {userMenu && (
              <div className="absolute bottom-full left-0 mb-2 w-full rounded-2xl border border-ink/[0.08] bg-white p-1.5 shadow-[0_12px_32px_rgba(16,24,40,0.14)]" data-testid="admin-user-menu">
                <button type="button" onClick={() => { setUserMenu(false); navigate("/admin/app/settings"); }}
                  data-testid="user-menu-account"
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-ink transition-colors hover:bg-ink/[0.04]">
                  <Settings className="h-4 w-4 text-slate" strokeWidth={1.5} /> Compte & sécurité
                </button>
                <button type="button" onClick={logout} data-testid="admin-logout-button"
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50">
                  <LogOut className="h-4 w-4" strokeWidth={1.5} /> Déconnexion
                </button>
              </div>
            )}
            <button type="button" onClick={() => setUserMenu(!userMenu)} data-testid="admin-user-button"
              className="flex w-full items-center gap-3 rounded-xl border border-ink/[0.06] bg-white px-3 py-2.5 text-left transition-colors hover:border-miles/25">
              <Avatar firstName={firstName} lastName={user?.name?.split(" ").slice(1).join(" ")} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink">{firstName}</span>
                <span className="block truncate text-xs text-slate">Super Admin</span>
              </span>
            </button>
          </div>
        </div>
      </aside>

      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="admin-drawer">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]" onClick={() => setDrawer(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-white">
            <div className="flex h-16 items-center justify-between px-5">
              <Logo variant="blue" className="h-[22px]" />
              <button type="button" onClick={() => setDrawer(false)} aria-label="Fermer le menu" data-testid="admin-drawer-close">
                <X className="h-5 w-5 text-slate" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 overflow-auto px-3 pb-4">{navContent(() => setDrawer(false), "mobile-")}</nav>
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col lg:pl-[248px]">
        <header className={`sticky top-0 z-30 flex h-16 items-center gap-3 px-4 transition-all duration-200 sm:px-6 ${
          scrolled ? "border-b border-ink/[0.06] bg-white/85 backdrop-blur-xl" : "bg-transparent"}`}>
          <button type="button" onClick={() => setDrawer(true)} className="lg:hidden" aria-label="Ouvrir le menu" data-testid="admin-menu-button">
            <Menu className="h-5 w-5 text-ink" strokeWidth={1.5} />
          </button>
          <CommandBar />
          <div className="ml-auto flex items-center gap-2">
            <div ref={bellRef} className="relative">
              <button type="button" onClick={() => setBellOpen(!bellOpen)} data-testid="admin-bell-button" aria-label="Notifications"
                className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-ink/[0.08] bg-white text-slate transition-colors duration-150 hover:text-ink">
                <Bell className="h-[17px] w-[17px]" strokeWidth={1.5} />
                {attention > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-miles px-1 text-[10px] font-bold text-white" data-testid="admin-bell-count">
                    {attention}
                  </span>
                )}
              </button>
              {bellOpen && (
                <div className="absolute right-0 top-[52px] z-50 w-[340px] rounded-2xl border border-ink/[0.08] bg-white/95 shadow-[0_16px_40px_rgba(16,24,40,0.14)] backdrop-blur-xl" data-testid="admin-bell-panel">
                  <p className="px-4 pb-1 pt-3.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate/60">Notifications · {attention}</p>
                  <div className="max-h-[380px] overflow-auto p-1.5">
                    {alerts.length === 0 && (
                      <p className="px-3 py-6 text-center text-sm text-slate" data-testid="bell-empty">Aucune alerte — opérations nominales.</p>
                    )}
                    {alerts.slice(0, 12).map((a, i) => (
                      <button key={i} type="button"
                        onClick={() => { setBellOpen(false); if (a.booking_id) navigate(`/admin/app/bookings/${a.booking_id}`); }}
                        data-testid={`bell-alert-${i}`}
                        className="flex w-full items-start gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-miles-ice/50">
                        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${LEVEL_DOT[a.level]}`} />
                        <span className="min-w-0">
                          <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate/60">{LEVEL_GROUP[a.level]}</span>
                          <span className="block truncate text-sm text-ink">{a.message}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button type="button" onClick={() => navigate("/admin/app/bookings/new")} data-testid="admin-new-booking-button"
              className="hidden h-10 items-center gap-2 rounded-xl bg-miles px-4 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(36,87,255,0.25)] transition-all duration-150 hover:bg-miles-dark active:scale-[0.98] sm:inline-flex">
              <Plus className="h-4 w-4" strokeWidth={2} /> Réservation
            </button>
          </div>
        </header>
        <main className="flex-1 px-4 pb-8 pt-2 sm:px-6">
          <Outlet context={{ user }} />
        </main>
      </div>

      {/* Actions rapides mobile (urgences) — masqué sur desktop */}
      <div className="sm:hidden" data-testid="admin-mobile-fab">
        {fabOpen && (
          <button type="button" aria-label="Fermer les actions" onClick={() => setFabOpen(false)}
            className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-[2px]" data-testid="admin-fab-backdrop" />
        )}
        <div className="fixed bottom-5 right-4 z-50 flex flex-col items-end gap-2.5">
          {fabOpen && (
            <>
              {[
                { to: "/admin/app/bookings/new", label: "Réservation", icon: CalendarClock, testId: "fab-new-booking" },
                { to: "/admin/app/invoices/new", label: "Facture", icon: FileText, testId: "fab-new-invoice" },
                { to: "/admin/app/quotes/new", label: "Devis", icon: Quote, testId: "fab-new-quote" },
              ].map((a) => (
                <button key={a.to} type="button" data-testid={a.testId}
                  onClick={() => { setFabOpen(false); navigate(a.to); }}
                  className="flex items-center gap-2.5 rounded-full bg-white py-2.5 pl-4 pr-3 text-sm font-semibold text-ink shadow-[0_8px_24px_rgba(16,24,40,0.18)] active:scale-[0.97]">
                  {a.label}
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-miles-ice text-miles">
                    <a.icon className="h-4 w-4" strokeWidth={2} />
                  </span>
                </button>
              ))}
            </>
          )}
          <button type="button" onClick={() => setFabOpen((v) => !v)} data-testid="admin-fab-toggle"
            aria-label="Actions rapides" aria-expanded={fabOpen}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-miles text-white shadow-[0_10px_30px_rgba(36,87,255,0.4)] transition-transform active:scale-95">
            {fabOpen ? <X className="h-6 w-6" strokeWidth={2} /> : <Plus className="h-6 w-6" strokeWidth={2} />}
          </button>
        </div>
      </div>
    </div>
  );
}
